import {
  TypeComparisonCardWithoutUnresolvableLinksResponse,
  TypeComparisonCardsWithoutUnresolvableLinksResponse,
} from 'contentful-types'
import { withContentful } from 'src/siteBuilder/renderer/contentful/withContentful'
import { BlockRenderer } from '@/siteBuilder/renderer/contentful/BlockRenderer'
import { typedLowerCase } from '@/siteBuilder/utils/text/mappers'
import ComparisonCards, { DISPLAY_ALWAYS, TOGGLE_OFF, TOGGLE_ON } from './ComparisonCards'

const MAP_PADDING = { Narrow: 'narrow', Intermediate: 'intermediate', Compact: 'compact', Default: 'custom' }

const MAP_CARD_STYLE = {
  Default: 'default',
  Grid: 'grid',
  Light: 'light',
  Lighter: 'lighter',
  Lightest: 'lightest',
  Dark: 'dark',
  Darker: 'darker',
  Darkest: 'darkest',
  Feature: 'feature',
  'Feature 2': 'feature2',
  'Feature 3': 'feature3',
  'Feature 4': 'feature4',
}

const mapFilterOption = (filter?: string) => {
  if (filter === 'Display when toggle is OFF') {
    return TOGGLE_OFF
  }
  if (filter === 'Display when toggle is ON') {
    return TOGGLE_ON
  }
  // If "Display in any toggle state" or "Not applicable" are selected in Contentful, or the filter is undefined, the default will be display always
  return DISPLAY_ALWAYS
}

const mapCards = (cards: TypeComparisonCardWithoutUnresolvableLinksResponse[]) => {
  if (!cards) return []

  const transformedCards = cards?.map((card) => ({
    components: <BlockRenderer block={card.fields?.components} fieldPath={'.components'} />,
    internetStateFilter: mapFilterOption(card.fields?.internetStateFilter),
    termStateFilter: mapFilterOption(card.fields?.termStateFilter),
    discountStateFilter: mapFilterOption(card.fields?.discountStateFilter),
    padding: MAP_PADDING[card.fields?.padding],
    borderRadius: typedLowerCase(card.fields?.borderRadius),
    cardStyle: MAP_CARD_STYLE[card.fields?.cardStyle],
    img: {
      src: card.fields?.img?.fields?.file?.url,
      width: card.fields?.img?.fields?.file?.details?.image?.width,
      height: card.fields?.img?.fields?.file?.details?.image?.height,
      alt: card.fields?.img?.fields?.description,
    },
    imgStyle: card.fields?.imgStyle?.toLowerCase().split(' ').join('_'),
    isDecorative: card.fields?.isDecorative,
    imgAltText: card.fields?.imgAltText,
    fullBleedImagePosition: {
      xs: typedLowerCase(card.fields?.xsFullBleedPositionOptions),
      sm: typedLowerCase(card.fields?.smFullBleedPositionOptions),
      md: typedLowerCase(card.fields?.mdFullBleedPositionOptions),
      lg: typedLowerCase(card.fields?.lgFullBleedPositionOptions),
      xl: typedLowerCase(card.fields?.xlFullBleedPositionOptions),
    },
    fullBleedContentAlignment: {
      xs: typedLowerCase(card.fields?.xsFullBleedAlignment),
      sm: typedLowerCase(card.fields?.smFullBleedAlignment),
      md: typedLowerCase(card.fields?.mdFullBleedAlignment),
      lg: typedLowerCase(card.fields?.lgFullBleedAlignment),
      xl: typedLowerCase(card.fields?.xlFullBleedAlignment),
    },
    backgroundImageResizeOption: {
      xs: typedLowerCase(card.fields?.xsBgImgResizeOptions),
      sm: typedLowerCase(card.fields?.smBgImgResizeOptions),
      md: typedLowerCase(card.fields?.mdBgImgResizeOptions),
      lg: typedLowerCase(card.fields?.lgBgImgResizeOptions),
      xl: typedLowerCase(card.fields?.xlBgImgResizeOptions),
    },
  }))

  return transformedCards
}

const ContentfulComparisonCards = withContentful<TypeComparisonCardsWithoutUnresolvableLinksResponse['fields']>(
  ComparisonCards,
  {
    cards: (props) => mapCards(props.fields?.cards),
    internetToggle: (props) => props.fields?.internetToggle?.fields,
    termToggle: (props) => props.fields?.termToggle?.fields,
    discountToggle: (props) => props.fields?.discountToggle?.fields,
    toggleConditions: (props) => {
      return (
        props.fields?.toggleConditions?.map((condition) => ({
          internet: condition?.fields?.internetState?.toLowerCase().split(' ').join('_'),
          term: condition?.fields?.termState?.toLowerCase().split(' ').join('_'),
          discount: condition?.fields?.discountState?.toLowerCase().split(' ').join('_'),
        })) || []
      )
    },
    hideInactiveToggle: (props) => props.fields?.hideInactiveToggle,
    noSavingsText: (props) => props.fields?.noSavingsText,
    oneSavingsText: (props) => props.fields?.oneSavingsText,
    twoSavingsText: (props) => props.fields?.twoSavingsText,
    savingsIcon: (props) => props.fields?.savingsIcon,
    isMobSavingsExpanded: (props) => props.fields?.isMobSavingsExpanded,
    xsColSpan: (props) => props.fields?.xsColSpan,
    smColSpan: (props) => props.fields?.smColSpan,
    mdColSpan: (props) => props.fields?.mdColSpan,
    lgColSpan: (props) => props.fields?.lgColSpan,
    xlColSpan: (props) => props.fields?.xlColSpan,
    equalHeightCards: (props) => props.fields?.equalHeightCards,
  }
)

export default ContentfulComparisonCards
